import { useSyncExternalStore } from 'react'
import {
  hasStoredPreferences,
  loadPreferences,
  savePreferences,
  type KeyValueStorage,
  type Preferences,
} from '../storage/preferences'

/**
 * Las preferencias de la app como un almacén reactivo. Se leen una vez al arrancar y el tema se
 * aplica antes de pintar, para que no haya un destello con el tema equivocado.
 */
function browserStorage(): KeyValueStorage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** En un móvil, la letra empieza más pequeña: cada dispositivo guarda sus propias preferencias. */
const PHONE_WIDTH = 520
const PHONE_FONT_SIZE = 20

function initialPreferences(): Preferences {
  const storage = browserStorage()
  const loaded = loadPreferences(storage)
  if (!hasStoredPreferences(storage) && window.innerWidth <= PHONE_WIDTH) {
    return { ...loaded, fontSize: PHONE_FONT_SIZE }
  }
  return loaded
}

let current: Preferences = initialPreferences()
const listeners = new Set<() => void>()

export function applyTheme(preferences: Preferences): void {
  document.documentElement.dataset.theme = preferences.theme
}

export function initPreferences(): void {
  applyTheme(current)
}

export function getPreferences(): Preferences {
  return current
}

export function updatePreferences(update: (preferences: Preferences) => Preferences): void {
  current = update(current)
  savePreferences(browserStorage(), current)
  applyTheme(current)
  listeners.forEach((listener) => listener())
}

/** Sustituye todas las preferencias (al restaurar una copia de seguridad). */
export function replacePreferences(next: Preferences): void {
  updatePreferences(() => next)
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function usePreferences(): Preferences {
  return useSyncExternalStore(subscribe, getPreferences)
}
